// src/components/Modals/UtilitiesModal.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { UtilitiesContext } from "../../contexts/UtilitiesContext";

const DEFAULT_TYPES = [
  "Electricity","Water","Gas","Internet","Phone (Landline)","Mobile/Cell",
  "Sewage","Rubbish","Recycling","Heating Oil","Propane",
  "Rent","Mortgage (Premises)","Strata / Body Corporate","Council / Property Rates",
  "Security Monitoring","Cleaning","Pest Control",
  "Web Hosting","Domain","CDN","Email Hosting","Cloud / Servers",
  "Accounting Software","Payroll Software","Point of Sale","Inventory Software",
  "Design / Creative Tools","Video Conferencing","CRM",
  "Building Insurance","Contents Insurance","Public Liability Insurance",
  "Workers Compensation","Professional Indemnity","Bank Fees",
  "Fuel","Vehicle Lease","Vehicle Insurance","Courier","Postage / Shipping",
  "Kiln Maintenance","Kiln Elements","Compressed Air","Dust Extraction",
  "Water Filtration","Glaze Disposal","Wastewater","Fire Safety Service",
];

const FREQUENCIES = ["Weekly", "Fortnightly", "Monthly", "Quarterly", "Yearly"];
const LS_TYPES = "mudoUtilityTypes"; // stores custom types the user adds

export default function UtilitiesModal({ isOpen, onClose }) {
  const { utilities, setUtilities } = useContext(UtilitiesContext);
  const [rows, setRows] = useState([]);
  const [customTypes, setCustomTypes] = useState([]);

  // keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        saveAll();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, rows]);

  // load custom types once
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_TYPES) || "[]");
      setCustomTypes(Array.isArray(saved) ? saved : []);
    } catch {
      setCustomTypes([]);
    }
  }, []);

  // Load rows from context when modal opens
  useEffect(() => {
    if (isOpen) setRows(utilities);
  }, [isOpen, utilities]);

  const typeOptions = useMemo(() => {
    const set = new Set(
      [...DEFAULT_TYPES, ...customTypes].map((s) => String(s).trim()).filter(Boolean)
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [customTypes]);

  if (!isOpen) return null;

  const updateRow = (id, field, value) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const rememberType = (val) => {
    const v = String(val || "").trim();
    if (!v) return;
    if (!DEFAULT_TYPES.includes(v) && !customTypes.includes(v)) {
      const next = [...customTypes, v].slice(-200);
      setCustomTypes(next);
      try {
        localStorage.setItem(LS_TYPES, JSON.stringify(next));
      } catch {}
    }
  };

  const addRow = () => {
    setRows((rs) => [
      ...rs,
      { id: Date.now().toString(), type: "", frequency: "Monthly", cost: "" },
    ]);
  };

  const removeRow = (id) => setRows((rs) => rs.filter((r) => r.id !== id));

  const money = (n) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: "AUD" }).format(n || 0);

  const toWeekly = (u) => {
    const c = parseFloat(u.cost) || 0;
    switch (u.frequency) {
      case "Weekly": return c;
      case "Fortnightly": return c / 2;
      case "Monthly": return c / 4.333;
      case "Quarterly": return c / 13;
      case "Yearly": return c / 52;
      default: return 0;
    }
  };
  const toMonthly = (u) => toWeekly(u) * 4.333;
  const toYearly  = (u) => toWeekly(u) * 52;

  const totals = useMemo(() => {
    const w = rows.reduce((s, u) => s + toWeekly(u), 0);
    return {
      weekly: w,
      monthly: w * 4.333,
      yearly: w * 52,
    };
  }, [rows]);

  const validate = () => {
    // allow blank rows to be ignored; only validate filled ones
    const bad = rows.filter(
      (r) =>
        (r.type?.trim() && (parseFloat(r.cost) ?? NaN) < 0) ||
        (r.cost !== "" && !Number.isFinite(parseFloat(r.cost)))
    );
    return bad.length === 0;
  };

  const saveAll = () => {
    if (!validate()) {
      alert("Please ensure costs are valid numbers (and not negative).");
      return;
    }
    // Filter out empty rows (no type and no cost)
    const cleaned = rows.filter((r) => (r.type?.trim() || r.cost?.toString().trim()));
    setUtilities(cleaned);
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="utils-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="w-full max-w-2xl bg-zinc-900 text-white rounded-lg shadow-lg p-6 overflow-auto max-h-[90vh]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex justify-between items-center mb-4 border-b border-zinc-700 pb-2">
          <h2 id="utils-title" className="text-xl font-semibold">Utilities Management</h2>
          <button onClick={onClose} className="text-white text-sm leading-none px-2 py-1" aria-label="Close">
            Close
          </button>
        </div>

        {/* table */}
        <table className="w-full text-sm mb-4">
          <thead>
            <tr className="text-left text-zinc-400">
              <th className="w-[45%]">Type</th>
              <th className="w-[20%]">Frequency</th>
              <th className="w-[25%]">Cost</th>
              <th className="w-[10%] text-center">Remove</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-zinc-700 align-top">
                <td className="py-2">
                  <input
                    list="mudo-utility-type-options"
                    className="w-full p-2 bg-zinc-800 border border-zinc-700 rounded text-white"
                    placeholder="Type or pick…"
                    value={r.type || ""}
                    onChange={(e) => updateRow(r.id, "type", e.target.value)}
                    onBlur={(e) => rememberType(e.target.value)}
                  />
                  <datalist id="mudo-utility-type-options">
                    {typeOptions.map((t) => (
                      <option key={t} value={t} />
                    ))}
                  </datalist>
                  <div className="mt-1 text-[11px] text-white/50">
                    Start typing to search — enter a new name to add it.
                  </div>
                </td>

                <td className="py-2">
                  <select
                    className="w-full p-2 bg-zinc-800 border border-zinc-700 rounded text-white"
                    value={r.frequency}
                    onChange={(e) => updateRow(r.id, "frequency", e.target.value)}
                  >
                    {FREQUENCIES.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </td>

                <td className="py-2">
                  <div className="flex items-center gap-2">
                    <span className="opacity-70">A$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      className="w-full p-2 bg-zinc-800 border border-zinc-700 rounded text-white"
                      placeholder="0.00"
                      value={r.cost}
                      onChange={(e) => updateRow(r.id, "cost", e.target.value)}
                    />
                  </div>
                  <div className="mt-1 text-[11px] text-white/40">
                    {(() => {
                      const u = { ...r, cost: r.cost };
                      const w = toWeekly(u), m = toMonthly(u), y = toYearly(u);
                      if (!Number.isFinite(w) || (r.cost ?? "") === "") return "—";
                      return `≈ ${money(w)}/wk • ${money(m)}/mo • ${money(y)}/yr`;
                    })()}
                  </div>
                </td>

                <td className="py-2 text-center">
                      <button
                        onClick={() => removeRow(r.id)}
                        className="text-red-500 hover:text-red-400"
                        aria-label="Remove row"
                        title="Remove"
                      >
                        Remove
                      </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <button
          onClick={addRow}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded mb-4"
        >
          Add Utility
        </button>

        {/* totals summary */}
        <div className="pt-4 border-t border-zinc-700 text-gray-200 grid grid-cols-3 gap-2">
          <div><span className="opacity-70">Total Weekly:</span> <span className="font-medium">{money(totals.weekly)}</span></div>
          <div><span className="opacity-70">Total Monthly:</span> <span className="font-medium">{money(totals.monthly)}</span></div>
          <div><span className="opacity-70">Total Yearly:</span> <span className="font-medium">{money(totals.yearly)}</span></div>
        </div>

        {/* actions */}
        <div className="flex justify-end space-x-2 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-zinc-700 rounded text-zinc-200"
          >
            Cancel
          </button>
          <button
            onClick={saveAll}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
            title="⌘/Ctrl+S"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
